import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import {
  LocationsService,
  RolesService,
} from '@boma/supplier-portal/shared/services';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { fetch } from '@nx/angular';
import { debounceTime, map, tap, withLatestFrom } from 'rxjs/operators';
import * as LocationsActions from './locations.actions';

@Injectable()
export class LocationsEffects {
  loadLocations$ = createEffect(() =>
    this.actions$.pipe(
      debounceTime(1000),
      ofType(LocationsActions.loadLocations),
      withLatestFrom(this.store),
      fetch({
        run: (action, state: any) => {
          const { user } = state;
          const { locations } = state;
          return this.locationsService
            .loadLocations(
              user.selectedOrganizationId,
              locations.nextPage || locations.page,
              locations.perPage,
              action.query || locations.query,
              action.locationType || 'Physical'
            )
            .pipe(
              map((response) =>
                LocationsActions.loadLocationsSuccess({
                  payload: response,
                })
              )
            );
        },

        onError: (action, error) => {
          console.error('Error', error);
          return LocationsActions.loadLocationsFailure({ error });
        },
      })
    )
  );

  loadLocation$ = createEffect(() =>
    this.actions$.pipe(
      ofType(LocationsActions.loadLocation),
      withLatestFrom(this.store),
      fetch({
        run: (action, state: any) => {
          const { user } = state;
          return this.locationsService
            .loadLocation(user.selectedOrganizationId, action.id)
            .pipe(
              map((response) =>
                LocationsActions.loadLocationSuccess({
                  location: response,
                })
              )
            );
        },

        onError: (action, error) => {
          console.error('Error', error);
          return LocationsActions.loadLocationFailure({ error });
        },
      })
    )
  );

  createLocation$ = createEffect(() =>
    this.actions$.pipe(
      ofType(LocationsActions.createLocation),
      withLatestFrom(this.store),
      fetch({
        run: (action, state: any) => {
          const { user } = state;
          return this.locationsService
            .createLocation(user.selectedOrganizationId, action.location)
            .pipe(
              tap((response) =>
                this.router.navigate(['/', 'locations', response.id])
              ),
              map((response) =>
                LocationsActions.createLocationSuccess({
                  location: response,
                })
              )
            );
        },

        onError: (action, error) => {
          console.error('Error', error);
          return LocationsActions.createLocationFailure({ error });
        },
      })
    )
  );

  updateLocation$ = createEffect(() =>
    this.actions$.pipe(
      ofType(LocationsActions.updateLocation),
      withLatestFrom(this.store),
      fetch({
        run: (action, state: any) => {
          const { user } = state;
          const { locations } = state;
          return this.locationsService
            .updateLocation(
              user.selectedOrganizationId,
              locations.selectedId,
              action.location
            )
            .pipe(
              map((response) =>
                LocationsActions.updateLocationSuccess({
                  location: response,
                })
              )
            );
        },

        onError: (action, error) => {
          console.error('Error', error);
          return LocationsActions.updateLocationFailure({ error });
        },
      })
    )
  );

  loadRoles$ = createEffect(() =>
    this.actions$.pipe(
      ofType(LocationsActions.loadLocationRoles),
      withLatestFrom(this.store),
      fetch({
        run: (action, state: any) => {
          const { user } = state;
          return this.roleSvc
            .listRoles(user.selectedOrganizationId, '1', '25', '', 'Location')
            .pipe(
              map((response) =>
                LocationsActions.loadLocationRolesSuccess({ payload: response })
              )
            );
        },

        onError: (action, error) => {
          console.error('Error', error);
          return LocationsActions.loadLocationRolesFailure({ error });
        },
      })
    )
  );

  updateLocationPermissions$ = createEffect(() =>
    this.actions$.pipe(
      ofType(LocationsActions.updateLocationPermissions),
      withLatestFrom(this.store),
      fetch({
        run: (action, state: any) => {
          //console.log(action);
          const { user } = state;
          return this.locationsService
            .updateLocationPermissions(
              user.selectedOrganizationId,
              action.locationId,
              action.userId,
              action.roleIds
            )
            .pipe(
              map((response) =>
                LocationsActions.updateLocationPermissionsSuccess({
                  payload: response,
                })
              )
            );
        },

        onError: (action, error) => {
          console.error('Error', error);
          return LocationsActions.updateLocationPermissionsFailure({ error });
        },
      })
    )
  );

  loadAdministrators$ = createEffect(() =>
    this.actions$.pipe(
      ofType(LocationsActions.loadLocationAdministrators),
      withLatestFrom(this.store),
      fetch({
        run: (action, state: any) => {
          const { user } = state;
          return this.locationsService
            .listAdministrators(
              user.selectedOrganizationId,
              action.locationId,
              action.query
            )
            .pipe(
              map((response) =>
                LocationsActions.loadLocationAdministratorsSuccess({
                  administrators: response,
                })
              )
            );
        },

        onError: (action, error) => {
          console.error('Error', error);
          return LocationsActions.loadLocationAdministratorsFailure({ error });
        },
      })
    )
  );

  loadAdministrator$ = createEffect(() =>
    this.actions$.pipe(
      ofType(LocationsActions.loadLocationAdministratorDetails),
      withLatestFrom(this.store),
      fetch({
        run: (action, state: any) => {
          const { user } = state;
          return this.locationsService
            .loadAdministratorDetails(
              user.selectedOrganizationId,
              action.locationId,
              action.adminId
            )
            .pipe(
              map((response) =>
                LocationsActions.loadLocationAdministratorDetailsSuccess({
                  administrator: response,
                })
              )
            );
        },

        onError: (action, error) => {
          console.error('Error', error);
          return LocationsActions.loadLocationAdministratorDetailsFailure({
            error,
          });
        },
      })
    )
  );

  deleteAdministrator$ = createEffect(() =>
    this.actions$.pipe(
      ofType(LocationsActions.deleteLocationAdministrator),
      withLatestFrom(this.store),
      fetch({
        run: (action, state: any) => {
          const { user } = state;
          return this.locationsService
            .deleteAdministrator(
              user.selectedOrganizationId,
              action.locationId,
              action.userId
            )
            .pipe(
              map((response) =>
                LocationsActions.deleteLocationAdministratorSuccess({
                  userId: action.userId,
                })
              )
            );
        },

        onError: (action, error) => {
          console.error('Error', error);
          return LocationsActions.loadLocationAdministratorDetailsFailure({
            error,
          });
        },
      })
    )
  );

  createAdministrator$ = createEffect(() =>
    this.actions$.pipe(
      ofType(LocationsActions.createLocationAdministrator),
      withLatestFrom(this.store),
      fetch({
        run: (action, state: any) => {
          const { user } = state;
          return this.locationsService
            .createAdministrator(
              user.selectedOrganizationId,
              action.locationId,
              action.emailAddressOrPhoneNumber,
              action.roleIds
            )
            .pipe(
              map((response) =>
                LocationsActions.createLocationAdministratorSuccess({
                  administrator: response,
                })
              )
            );
        },
        onError: (action, error) => {
          console.error('Error', error);
          return LocationsActions.loadLocationAdministratorDetailsFailure({
            error,
          });
        },
      })
    )
  );

  updateAdministrator$ = createEffect(() =>
    this.actions$.pipe(
      ofType(LocationsActions.updateLocationAdministrator),
      withLatestFrom(this.store),
      fetch({
        run: (action, state: any) => {
          const { user } = state;
          return this.locationsService
            .updateAdministrator(
              user.selectedOrganizationId,
              action.locationId,
              action.userId,
              action.roleIds
            )
            .pipe(
              map((response) =>
                LocationsActions.updateLocationAdministratorSuccess({
                  administrator: response,
                })
              )
            );
        },
        onError: (action, error) => {
          console.error('Error', error);
          return LocationsActions.loadLocationAdministratorDetailsFailure({
            error,
          });
        },
      })
    )
  );

  constructor(
    private actions$: Actions,
    private store: Store,
    private locationsService: LocationsService,
    private roleSvc: RolesService,
    private router: Router
  ) {}
}
